import React, { useState, useContext } from "react";
import axios from "axios";
import axiosInstance from "./axiosInstance";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext } from "./AuthContext ";
import { getApiErrorMessage } from '../utils/apiError';

const AdminLoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setToken, setRefreshToken } = useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);

    const user = { username, password };

    try {
      // Get JWT tokens
      const { data } = await axiosInstance.post("token/", user, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      // Save tokens in context & localStorage
      setToken(data.access);
      setRefreshToken(data.refresh);
      localStorage.setItem("token", data.access);
      localStorage.setItem("refreshToken", data.refresh);
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.access}`;

      // Fetch user data to confirm admin privileges
      const userRes = await axiosInstance.get("user/");
      console.log('Admin login user data:', userRes.data);
      const isAdmin = userRes.data.is_staff || userRes.data.is_superuser;

      if (!isAdmin) {
        // Not an admin: clear tokens and show error
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        delete axios.defaults.headers.common["Authorization"];
        setErrorMessage("This account is not an admin. Please use a super admin account.");
        return;
      }

      // Admin confirmed: go directly to admin dashboard
      navigate("/admin-sereni");
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'Admin login failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 px-4 mt-[95px]">
      <div className="absolute top-0 right-0 w-48 h-48 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
      <div className="absolute top-0 left-1/4 w-48 h-48 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>

      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="px-8 py-10">
            <h2 className="mb-6 text-3xl font-bold text-center text-gray-800">
              SereniMind <span className="text-[#59844F]">Admin Login</span>
            </h2>
            <p className="mb-6 text-sm text-center text-gray-500">
              Use your super admin credentials to access the admin panel.
            </p>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="username" className="block mb-2 text-sm font-medium text-gray-700">
                  Admin Username
                </label>
                <input
                  type="text"
                  id="username"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#59844F] focus:border-transparent transition"
                  placeholder="Enter admin username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-700">
                  Admin Password
                </label>
                <input
                  type="password"
                  id="password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#59844F] focus:border-transparent transition"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {errorMessage && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded whitespace-pre-line" role="alert">
                  <p>{errorMessage}</p>
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-[#59844F] text-white py-3 px-4 rounded-md hover:bg-[#4b7443] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#59844F] transition duration-200 ease-in-out transform hover:-translate-y-1 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 mx-auto text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  "Login as Admin"
                )}
              </button>
            </form>
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600">
                Need the regular user login?{' '}
                <Link to="/login" className="font-medium text-[#59844F] hover:text-[#4b7443] transition">
                  Go to user login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
